/**
 * Correos transaccionales — `OD-0066`.
 *
 * Contesta «¿le llegó el correo?» sin abrir el panel del proveedor: qué correo,
 * cuándo, a quién (enmascarado) y si el proveedor lo aceptó o falló. Solo
 * lectura y solo `GLOBAL_DB`, así que mirarlo no deja acceso anotado en la
 * auditoría de ningún cliente.
 *
 * La misma tabla sirve para la lista general (`Correos`) y para la ficha de una
 * empresa (`CorreosDeEmpresa`). La ficha no trae las altas: cuando se mandó el
 * correo de verificación todavía no había empresa.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type CorreoEnviado } from "../api/cliente";
import { t } from "../textos";

function fechaLegible(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function estadoDe(c: CorreoEnviado): string {
  if (c.status === "FAILED") return t.correos.fallo(c.failureReason);
  return t.correos.estados[c.status] ?? c.status;
}

function Destinatario({ c }: { c: CorreoEnviado }) {
  if (c.recipientMasked === null) return <span className="suave" title={t.correos.sinDatoDetalle}>{t.correos.sinDato}</span>;
  return (
    <>
      {c.recipientMasked}
      {c.recipientRole !== null ? (
        <>
          <br />
          <span className="suave">{t.correos.roles[c.recipientRole] ?? c.recipientRole}</span>
        </>
      ) : null}
    </>
  );
}

function TablaCorreos({ correos, conEmpresa, onAbrirEmpresa }: {
  correos: readonly CorreoEnviado[];
  conEmpresa: boolean;
  onAbrirEmpresa?: (id: string) => void;
}) {
  if (correos.length === 0) return <p className="suave">{t.correos.vacio}</p>;
  return (
    <table>
      <thead>
        <tr>
          <th>{t.correos.columnas.cuando}</th>
          <th>{t.correos.columnas.correo}</th>
          <th>{t.correos.columnas.destinatario}</th>
          {conEmpresa ? <th>{t.correos.columnas.empresa}</th> : null}
          <th>{t.correos.columnas.estado}</th>
          <th>{t.correos.columnas.proveedor}</th>
        </tr>
      </thead>
      <tbody>
        {correos.map((c, i) => (
          <tr key={`${c.createdAt}-${i}`}>
            <td>{fechaLegible(c.createdAt)}</td>
            <td>{t.correos.tipos[c.kind] ?? c.kind}</td>
            <td>
              <Destinatario c={c} />
            </td>
            {conEmpresa ? (
              <td>
                {c.organizationId === null ? (
                  <span className="suave">—</span>
                ) : onAbrirEmpresa !== undefined ? (
                  <button type="button" data-variante="secundario" onClick={() => onAbrirEmpresa(c.organizationId ?? "")}>
                    {c.organizationName ?? c.organizationId}
                  </button>
                ) : (
                  (c.organizationName ?? c.organizationId)
                )}
              </td>
            ) : null}
            <td>{estadoDe(c)}</td>
            <td className="suave">{c.providerMessageId ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type Estado = "cargando" | "ok" | "no-encontrado" | "sin-mfa" | "error";

function useCorreos(organizationId: string | null) {
  const [estado, setEstado] = useState<Estado>("cargando");
  const [correos, setCorreos] = useState<readonly CorreoEnviado[]>([]);

  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.correos(organizationId);
    if (r.ok) {
      setCorreos(r.datos);
      setEstado("ok");
      return;
    }
    if (r.estado === 404 || r.estado === 401) setEstado("no-encontrado");
    else if (r.estado === 403) setEstado("sin-mfa");
    else setEstado("error");
  }, [organizationId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  return { estado, correos };
}

function Aviso({ estado }: { estado: Estado }) {
  if (estado === "cargando") return <p className="suave">{t.comun.cargando}</p>;
  if (estado === "no-encontrado") return <p>{t.comun.noEncontrado}</p>;
  if (estado === "sin-mfa") return <p className="aviso" data-tono="aviso">{t.comun.sinMfa}</p>;
  return <p className="aviso" data-tono="error">{t.comun.error}</p>;
}

/** La lista general: los últimos correos de todo el mundo, altas incluidas. */
export function Correos({ onAbrirEmpresa }: { onAbrirEmpresa: (id: string) => void }) {
  const { estado, correos } = useCorreos(null);
  return (
    <>
      <h1>{t.correos.titulo}</h1>
      <p className="aviso" data-tono="aviso">{t.correos.explicacion}</p>
      {estado === "ok" ? <TablaCorreos correos={correos} conEmpresa onAbrirEmpresa={onAbrirEmpresa} /> : <Aviso estado={estado} />}
    </>
  );
}

/** Para la ficha de una empresa: los suyos y los de sus usuarios. */
export function CorreosDeEmpresa({ organizationId }: { organizationId: string }) {
  const { estado, correos } = useCorreos(organizationId);
  return (
    <>
      <h2>{t.correos.tituloEmpresa}</h2>
      <p className="suave">{t.correos.explicacionEmpresa}</p>
      {estado === "ok" ? <TablaCorreos correos={correos} conEmpresa={false} /> : <Aviso estado={estado} />}
    </>
  );
}
